import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Scenario, Scene } from '../../types';
import { useInlineClipboard } from './inline-clipboard-context';
import { INLINE_INSERT_TYPES } from './inline-insert-shared';
import { InlineAiGenerateForm } from './InlineAiGenerateForm';
import type { AiBlockContext } from './ai-block-guides';
import inline from './inline-editor.module.css';
import studio from './scenario-studio.module.css';

export type InlineInsertTypeEntry = { type: string; label: string; variant?: string };

/** undefined = closed, null = chapter start, string = after block id */
export type InsertAfterBlockId = string | null | undefined;

export type InlineInsertMode = 'manual' | 'ai' | 'paste';

export interface InlineInsertContextValue {
  insertAfterBlockId: InsertAfterBlockId;
  insertTypes: InlineInsertTypeEntry[];
  aiContext?: AiBlockContext;
  scenario?: Scenario;
  scenarioId?: string;
  onOpenSettings?: () => void;
  requestInsertAfter: (blockId: string) => void;
  cancelInsert: () => void;
  confirmInsert: (type: string, variant: string | undefined, text: string) => void;
  confirmInsertGenerated: (body: Omit<Scene, 'id'>) => void;
  confirmInsertPasted: () => void;
}

export const InlineInsertContext = createContext<InlineInsertContextValue | null>(null);

interface InlineInsertProviderProps {
  /** Insert a new scene after the given scene id (null = at chapter start). */
  onInsertAfter: (afterSceneId: string | null, type: string, variant?: string, text?: string) => void;
  onInsertGenerated?: (afterSceneId: string | null, body: Omit<Scene, 'id'>) => void;
  onInsertPasted?: (afterSceneId: string | null, body: Omit<Scene, 'id'>) => void;
  insertTypes?: InlineInsertTypeEntry[];
  aiContext?: AiBlockContext;
  scenario?: Scenario;
  scenarioId?: string;
  onOpenSettings?: () => void;
  children: React.ReactNode;
}

export const InlineInsertProvider: React.FC<InlineInsertProviderProps> = ({
  onInsertAfter,
  onInsertGenerated,
  onInsertPasted,
  insertTypes = INLINE_INSERT_TYPES,
  aiContext,
  scenario,
  scenarioId,
  onOpenSettings,
  children,
}) => {
  const [insertAfterBlockId, setInsertAfterBlockId] = useState<InsertAfterBlockId>(undefined);
  const clipboard = useInlineClipboard();

  const requestInsertAfter = useCallback((blockId: string) => {
    setInsertAfterBlockId((prev) => (prev === blockId ? undefined : blockId));
  }, []);

  const cancelInsert = useCallback(() => setInsertAfterBlockId(undefined), []);

  const confirmInsert = useCallback(
    (type: string, variant: string | undefined, text: string) => {
      if (insertAfterBlockId === undefined) return;
      onInsertAfter(insertAfterBlockId, type, variant, text);
      setInsertAfterBlockId(undefined);
    },
    [insertAfterBlockId, onInsertAfter]
  );

  const confirmInsertGenerated = useCallback(
    (body: Omit<Scene, 'id'>) => {
      if (insertAfterBlockId === undefined || !onInsertGenerated) return;
      onInsertGenerated(insertAfterBlockId, body);
      setInsertAfterBlockId(undefined);
    },
    [insertAfterBlockId, onInsertGenerated]
  );

  const confirmInsertPasted = useCallback(() => {
    if (insertAfterBlockId === undefined) return;
    const body = clipboard?.getPasteBody();
    if (!body) return;
    const handler = onInsertPasted ?? onInsertGenerated;
    if (!handler) return;
    handler(insertAfterBlockId, body);
    setInsertAfterBlockId(undefined);
  }, [insertAfterBlockId, clipboard, onInsertPasted, onInsertGenerated]);

  const value = useMemo(
    () => ({
      insertAfterBlockId,
      insertTypes,
      aiContext,
      scenario,
      scenarioId,
      onOpenSettings,
      requestInsertAfter,
      cancelInsert,
      confirmInsert,
      confirmInsertGenerated,
      confirmInsertPasted,
    }),
    [
      insertAfterBlockId,
      insertTypes,
      aiContext,
      scenario,
      scenarioId,
      onOpenSettings,
      requestInsertAfter,
      cancelInsert,
      confirmInsert,
      confirmInsertGenerated,
      confirmInsertPasted,
    ]
  );

  return <InlineInsertContext.Provider value={value}>{children}</InlineInsertContext.Provider>;
};

export const InlineInsertSlot: React.FC<{ blockId: string }> = ({ blockId }) => {
  const ctx = useContext(InlineInsertContext);
  if (!ctx || ctx.insertAfterBlockId !== blockId) return null;

  return (
    <InlineInsertForm
      onAdd={ctx.confirmInsert}
      onInsertGenerated={ctx.confirmInsertGenerated}
      onInsertPasted={ctx.confirmInsertPasted}
      onCancel={ctx.cancelInsert}
      insertTypes={ctx.insertTypes}
      aiContext={ctx.aiContext}
      onOpenSettings={ctx.onOpenSettings}
    />
  );
};

interface InlineInsertFormProps {
  onAdd: (type: string, variant: string | undefined, text: string) => void;
  onInsertGenerated?: (body: Omit<Scene, 'id'>) => void;
  onInsertPasted?: () => void;
  onCancel: () => void;
  compact?: boolean;
  insertTypes?: InlineInsertTypeEntry[];
  aiContext?: AiBlockContext;
  onOpenSettings?: () => void;
}

export const InlineInsertForm: React.FC<InlineInsertFormProps> = ({
  onAdd,
  onInsertGenerated,
  onInsertPasted,
  onCancel,
  compact = false,
  insertTypes = INLINE_INSERT_TYPES,
  aiContext,
  onOpenSettings,
}) => {
  const insertCtx = useContext(InlineInsertContext);
  const clipboard = useInlineClipboard();
  const scenario = insertCtx?.scenario;
  const scenarioId = insertCtx?.scenarioId;
  const showAiTab = Boolean(onInsertGenerated);
  const showPasteTab = Boolean(clipboard?.hasClipboard && (onInsertPasted || insertCtx?.confirmInsertPasted));
  const [mode, setMode] = useState<InlineInsertMode>(
    showAiTab ? 'ai' : showPasteTab ? 'paste' : 'manual'
  );
  const [type, setType] = useState(insertTypes[0]?.type ?? 'dialogue');
  const [variant, setVariant] = useState<string | undefined>(insertTypes[0]?.variant);
  const [text, setText] = useState('');

  const handleTypeChange = (val: string) => {
    const entry = insertTypes.find((t) => `${t.type}:${t.variant ?? ''}` === val);
    if (entry) {
      setType(entry.type);
      setVariant(entry.variant);
    }
  };

  const confirm = () => {
    onAdd(type, variant, text);
    setText('');
  };

  const confirmPaste = () => {
    if (onInsertPasted) {
      onInsertPasted();
    } else if (insertCtx?.confirmInsertPasted) {
      insertCtx.confirmInsertPasted();
    }
  };

  const showModeTabs = showAiTab || showPasteTab;

  const manualForm = (
    <div className={inline.addForm}>
      <span className={inline.addFormLabel}>Nouvelle ligne</span>
      <select
        className={studio.select}
        value={`${type}:${variant ?? ''}`}
        onChange={(e) => handleTypeChange(e.target.value)}
      >
        {insertTypes.map((t) => (
          <option key={`${t.type}:${t.variant ?? ''}`} value={`${t.type}:${t.variant ?? ''}`}>
            {t.label}
          </option>
        ))}
      </select>
      <input
        className={`${studio.input} ${inline.addInput}`}
        placeholder="Texte initial (optionnel)…"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') confirm();
          if (e.key === 'Escape') onCancel();
        }}
        autoFocus={mode === 'manual'}
      />
      <button type="button" className={studio.btnPrimary} onClick={confirm}>
        Ajouter
      </button>
      <button type="button" className={studio.btnGhost} onClick={onCancel}>
        Annuler
      </button>
    </div>
  );

  const pasteForm = (
    <div className={inline.addForm}>
      <span className={inline.addFormLabel}>Coller le bloc copié</span>
      <span className={inline.pastePreview}>{clipboard?.clipboardLabel}</span>
      <button type="button" className={studio.btnPrimary} onClick={confirmPaste} autoFocus>
        Coller
      </button>
      <button type="button" className={studio.btnGhost} onClick={onCancel}>
        Annuler
      </button>
    </div>
  );

  const form = (
    <div className={inline.insertFormWrap}>
      {showModeTabs && (
        <div className={inline.insertModeTabs}>
          <button
            type="button"
            className={mode === 'manual' ? inline.insertModeTabActive : inline.insertModeTab}
            onClick={() => setMode('manual')}
          >
            Manuel
          </button>
          {showAiTab && (
            <button
              type="button"
              className={mode === 'ai' ? inline.insertModeTabActive : inline.insertModeTab}
              onClick={() => setMode('ai')}
            >
              Générer par IA
            </button>
          )}
          {showPasteTab && (
            <button
              type="button"
              className={mode === 'paste' ? inline.insertModeTabActive : inline.insertModeTab}
              onClick={() => setMode('paste')}
            >
              Coller
            </button>
          )}
        </div>
      )}

      {mode === 'ai' && onInsertGenerated ? (
        <InlineAiGenerateForm
          compact={compact}
          context={aiContext}
          scenario={scenario}
          scenarioId={scenarioId}
          onInsert={onInsertGenerated}
          onCancel={onCancel}
          onOpenSettings={onOpenSettings}
        />
      ) : mode === 'paste' && showPasteTab ? (
        pasteForm
      ) : (
        manualForm
      )}
    </div>
  );

  if (compact) return form;

  return <div className={inline.insertBelow}>{form}</div>;
};
